import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaGlobe,
  FaPlus,
  FaTrash,
  FaSave,
  FaUndo,
  FaUpload
} from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import ServiceHeroExtrasEditor from '../../components/cms/ServiceHeroExtrasEditor';

const CFOAdvisoryCMS = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    heroTitle: '',
    heroBody: '',
    heroImage: '',
    heroBtn1Text: '',
    heroBtn2Text: '',

    heroBadgeText: '',
    heroBadgeIcon: 'check',
    heroTrustBadges: [],

    servicesTitle: '',
    servicesSubtitle: '',
    services: [],

    whyAdvisoryTitle: '',
    whyAdvisoryBody1: '',
    whyAdvisoryBody2: '',

    dashboardTitle: '',
    dashboardSubtitle: '',
    dashboardBody: '',
    dashboardItems: [],

    stepsTitle: '',
    processSteps: [],

    beneficiariesTitle: '',
    beneficiaries: [],

    whyChooseTitle: '',
    whyChoose: [],

    faqsTitle: '',
    faqsSubtitle: '',
    faqs: [],

    isPublished: true
  });

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/cfo-advisory/admin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const data = res.data.data || {};
        setForm({
          heroTitle: data.heroTitle || '',
          heroBody: data.heroBody || '',
          heroImage: data.heroImage || '',
          heroBtn1Text: data.heroBtn1Text || '',
          heroBtn2Text: data.heroBtn2Text || '',

          heroBadgeText: data.heroBadgeText || '',
          heroBadgeIcon: data.heroBadgeIcon || 'check',
          heroTrustBadges: data.heroTrustBadges || [],

          servicesTitle: data.servicesTitle || '',
          servicesSubtitle: data.servicesSubtitle || '',
          services: data.services || [],

          whyAdvisoryTitle: data.whyAdvisoryTitle || '',
          whyAdvisoryBody1: data.whyAdvisoryBody1 || '',
          whyAdvisoryBody2: data.whyAdvisoryBody2 || '',

          dashboardTitle: data.dashboardTitle || '',
          dashboardSubtitle: data.dashboardSubtitle || '',
          dashboardBody: data.dashboardBody || '',
          dashboardItems: data.dashboardItems || [],

          stepsTitle: data.stepsTitle || '',
          processSteps: data.processSteps || [],

          beneficiariesTitle: data.beneficiariesTitle || '',
          beneficiaries: data.beneficiaries || [],

          whyChooseTitle: data.whyChooseTitle || '',
          whyChoose: data.whyChoose || [],

          faqsTitle: data.faqsTitle || '',
          faqsSubtitle: data.faqsSubtitle || '',
          faqs: data.faqs || [],

          isPublished: data.isPublished !== false
        });
      }
    } catch (err) {
      toastError('Failed to load CFO Advisory page content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/cfo-advisory/admin`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('CFO Advisory page content saved successfully');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaults = async () => {
    if (!window.confirm('Are you sure you want to revert CFO Advisory page options to default?')) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/cfo-advisory/admin/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Restored default content successfully');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to restore defaults');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/cfo-advisory/admin/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (res.data.success) {
        updateField(field, res.data.url);
        toastSuccess('Image uploaded and optimized successfully');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to upload image');
    }
  };

  // Services helpers
  const addService = () => {
    setForm(prev => ({
      ...prev,
      services: [...prev.services, { title: '', description: '', detail: '' }]
    }));
  };

  const removeService = (idx) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== idx)
    }));
  };

  const updateServiceField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.services];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, services: list };
    });
  };

  // Dashboard Items helpers
  const addDashboardItem = () => {
    setForm(prev => ({
      ...prev,
      dashboardItems: [...prev.dashboardItems, { title: '', description: '' }]
    }));
  };

  const removeDashboardItem = (idx) => {
    setForm(prev => ({
      ...prev,
      dashboardItems: prev.dashboardItems.filter((_, i) => i !== idx)
    }));
  };

  const updateDashboardItemField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.dashboardItems];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, dashboardItems: list };
    });
  };

  // Timeline Steps helpers
  const addTimelineStep = () => {
    setForm(prev => ({
      ...prev,
      processSteps: [...prev.processSteps, '']
    }));
  };

  const removeTimelineStep = (idx) => {
    setForm(prev => ({
      ...prev,
      processSteps: prev.processSteps.filter((_, i) => i !== idx)
    }));
  };

  const updateTimelineStepField = (idx, val) => {
    setForm(prev => {
      const list = [...prev.processSteps];
      list[idx] = val;
      return { ...prev, processSteps: list };
    });
  };

  // Beneficiaries helpers
  const addBeneficiary = () => {
    setForm(prev => ({
      ...prev,
      beneficiaries: [...prev.beneficiaries, { title: '', body: '' }]
    }));
  };

  const removeBeneficiary = (idx) => {
    setForm(prev => ({
      ...prev,
      beneficiaries: prev.beneficiaries.filter((_, i) => i !== idx)
    }));
  };

  const updateBeneficiaryField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.beneficiaries];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, beneficiaries: list };
    });
  };

  // Why Choose helpers
  const addWhyChoose = () => {
    setForm(prev => ({
      ...prev,
      whyChoose: [...prev.whyChoose, { title: '', description: '' }]
    }));
  };

  const removeWhyChoose = (idx) => {
    setForm(prev => ({
      ...prev,
      whyChoose: prev.whyChoose.filter((_, i) => i !== idx)
    }));
  };

  const updateWhyChooseField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.whyChoose];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, whyChoose: list };
    });
  };

  // FAQs helpers
  const addFAQ = () => {
    setForm(prev => ({
      ...prev,
      faqs: [...prev.faqs, { question: '', answer: '' }]
    }));
  };

  const removeFAQ = (idx) => {
    setForm(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== idx)
    }));
  };

  const updateFAQField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.faqs];
      list[idx] = { ...list[idx], [key]: val };
      return { ...prev, faqs: list };
    });
  };

  if (loading) return <SkeletonLoader variant="page" />;

  return (
    <PageLayout>
      <PageHeader
        title="CFO Advisory Page CMS"
        description="Edit dynamic headings, side images, strategy services, metrics dashboard items, timeline workflows, target beneficiaries, advantages, and FAQs."
        icon={FaGlobe}
        actions={
          <div className="flex gap-3">
            <button
              onClick={handleRestoreDefaults}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2 animate-fade-in"
            >
              <FaUndo className="w-3.5 h-3.5" /> Revert Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-premium flex items-center gap-2 animate-fade-in"
            >
              <FaSave className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-dark-700 overflow-x-auto gap-2 mb-6 scrollbar-hide">
        {[
          { id: 'hero', name: 'Hero Banner' },
          { id: 'services', name: 'Strategy Services' },
          { id: 'necessity', name: 'CFO Necessity' },
          { id: 'dashboard', name: 'Live Dashboard' },
          { id: 'timeline', name: 'Work Steps' },
          { id: 'beneficiaries', name: 'CFO Targets' },
          { id: 'choose', name: 'Choose BooksComply' },
          { id: 'faqs', name: 'Page FAQs' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 font-bold text-sm border-b-2 whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-white'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {/* Tab 1: Hero Banner */}
        {activeTab === 'hero' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5 animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Hero Header Layout</h2>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) => updateField('isPublished', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Published
              </label>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Main Title</label>
              <input
                className="input-modern"
                value={form.heroTitle}
                onChange={(e) => updateField('heroTitle', e.target.value)}
                placeholder="Strategic Virtual CFO Services for Startups..."
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Description Body</label>
              <textarea
                className="input-modern min-h-[100px]"
                value={form.heroBody}
                onChange={(e) => updateField('heroBody', e.target.value)}
                placeholder="BooksComply’s Virtual CFO Services help U.S. startups..."
              />
            </div>

            {/* Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border-t border-b border-gray-100 dark:border-dark-700 py-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Primary Button Label</label>
                <input
                  className="input-modern text-sm"
                  value={form.heroBtn1Text}
                  onChange={(e) => updateField('heroBtn1Text', e.target.value)}
                  placeholder="See CFO Plans"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Secondary Button Label</label>
                <input
                  className="input-modern text-sm"
                  value={form.heroBtn2Text}
                  onChange={(e) => updateField('heroBtn2Text', e.target.value)}
                  placeholder="Book Free Consultation"
                />
              </div>
            </div>

            <ServiceHeroExtrasEditor form={form} updateField={updateField} />

            {/* Image Upload */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Side Image</label>
              <div className="flex items-center gap-4">
                <input
                  className="input-modern flex-1"
                  value={form.heroImage}
                  onChange={(e) => updateField('heroImage', e.target.value)}
                  placeholder="Image URL path..."
                />
                <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer whitespace-nowrap">
                  <FaUpload className="w-3.5 h-3.5" /> Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'heroImage')}
                    className="hidden"
                  />
                </label>
              </div>
              {form.heroImage && (
                <div className="mt-2 relative w-48 h-32 rounded-xl overflow-hidden border border-gray-200">
                  <img
                    src={form.heroImage.startsWith('http') ? form.heroImage : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${form.heroImage}`}
                    alt="Hero preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 2: Strategy Services */}
        {activeTab === 'services' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6 animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">CFO Services Offerings</h2>
              <button
                onClick={addService}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Service Card
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Header Title</label>
                <input
                  className="input-modern"
                  value={form.servicesTitle}
                  onChange={(e) => updateField('servicesTitle', e.target.value)}
                  placeholder="Comprehensive CFO Advisory..."
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Subtitle</label>
                <input
                  className="input-modern"
                  value={form.servicesSubtitle}
                  onChange={(e) => updateField('servicesSubtitle', e.target.value)}
                  placeholder="Strategic financial leadership..."
                />
              </div>
            </div>

            <div className="space-y-4 pt-4">
              {form.services.map((service, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeService(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Service Card #{idx + 1}</h3>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Title</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.title}
                        onChange={(e) => updateServiceField(idx, 'title', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Description</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[60px]"
                        value={service.description}
                        onChange={(e) => updateServiceField(idx, 'description', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Detail Bullet / Paragraph</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[50px]"
                        value={service.detail}
                        onChange={(e) => updateServiceField(idx, 'detail', e.target.value)}
                        placeholder="Additional details note..."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 3: CFO Necessity */}
        {activeTab === 'necessity' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5 animate-fade-in">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-3">Why Businesses Need CFO Advisory</h2>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Block Header Title</label>
              <input
                className="input-modern"
                value={form.whyAdvisoryTitle}
                onChange={(e) => updateField('whyAdvisoryTitle', e.target.value)}
                placeholder="Why Businesses Need CFO Advisory Services..."
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Body Paragraph 1</label>
              <textarea
                className="input-modern min-h-[100px]"
                value={form.whyAdvisoryBody1}
                onChange={(e) => updateField('whyAdvisoryBody1', e.target.value)}
                placeholder="Growing a business takes more than tracking expenses..."
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Body Paragraph 2</label>
              <textarea
                className="input-modern min-h-[80px]"
                value={form.whyAdvisoryBody2}
                onChange={(e) => updateField('whyAdvisoryBody2', e.target.value)}
                placeholder="BooksComply’s virtual CFO services give startups..."
              />
            </div>
          </div>
        )}

        {/* Tab 4: Live Dashboard */}
        {activeTab === 'dashboard' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6 animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">CFO Dashboard Section</h2>
              <button
                onClick={addDashboardItem}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Dashboard Item
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Title</label>
                <input
                  className="input-modern"
                  value={form.dashboardTitle}
                  onChange={(e) => updateField('dashboardTitle', e.target.value)}
                  placeholder="Gain Financial Clarity with Our Virtual CFO Dashboard..."
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Eyebrow Subtitle</label>
                <input
                  className="input-modern"
                  value={form.dashboardSubtitle}
                  onChange={(e) => updateField('dashboardSubtitle', e.target.value)}
                  placeholder="Transparency & Trust..."
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Dashboard Body Text</label>
                <input
                  className="input-modern"
                  value={form.dashboardBody}
                  onChange={(e) => updateField('dashboardBody', e.target.value)}
                  placeholder="Track every critical metric inside one secure platform..."
                />
              </div>
            </div>

            <div className="space-y-4 pt-4">
              {form.dashboardItems.map((item, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeDashboardItem(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Dashboard Item #{idx + 1}</h3>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Title</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={item.title}
                        onChange={(e) => updateDashboardItemField(idx, 'title', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Description</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[60px]"
                        value={item.description}
                        onChange={(e) => updateDashboardItemField(idx, 'description', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 5: Work Steps */}
        {activeTab === 'timeline' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6 animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">CFO Workflow Timeline</h2>
              <button
                onClick={addTimelineStep}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Guide Step
              </button>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Timeline Section Title</label>
              <input
                className="input-modern"
                value={form.stepsTitle}
                onChange={(e) => updateField('stepsTitle', e.target.value)}
                placeholder="How Our Virtual CFO Services Work..."
              />
            </div>

            <div className="space-y-3">
              {form.processSteps.map((step, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <span className="text-sm font-bold text-gray-400">Step #{idx + 1}</span>
                  <input
                    className="input-modern text-sm py-1.5 px-3 flex-1"
                    value={step}
                    onChange={(e) => updateTimelineStepField(idx, e.target.value)}
                    placeholder="Describe this timeline step..."
                  />
                  <button
                    onClick={() => removeTimelineStep(idx)}
                    className="p-2 text-red-500 border rounded-lg hover:bg-red-50"
                  >
                    <FaTrash className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 6: CFO Targets */}
        {activeTab === 'beneficiaries' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6 animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Target Beneficiaries</h2>
              <button
                onClick={addBeneficiary}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Beneficiary Card
              </button>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Beneficiaries Title</label>
              <input
                className="input-modern"
                value={form.beneficiariesTitle}
                onChange={(e) => updateField('beneficiariesTitle', e.target.value)}
                placeholder="Who Benefits from Virtual CFO Advisory..."
              />
            </div>

            <div className="space-y-4 pt-4">
              {form.beneficiaries.map((benefit, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeBeneficiary(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Target #{idx + 1}</h3>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Heading Title</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={benefit.title}
                        onChange={(e) => updateBeneficiaryField(idx, 'title', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Target Body Detail</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[60px]"
                        value={benefit.body}
                        onChange={(e) => updateBeneficiaryField(idx, 'body', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 7: Choose BooksComply */}
        {activeTab === 'choose' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6 animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Choose BooksComply Reasons</h2>
              <button
                onClick={addWhyChoose}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Reason Card
              </button>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Why Choose Title</label>
              <input
                className="input-modern"
                value={form.whyChooseTitle}
                onChange={(e) => updateField('whyChooseTitle', e.target.value)}
                placeholder="Why Choose BooksComply..."
              />
            </div>

            <div className="space-y-4 pt-4">
              {form.whyChoose.map((item, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeWhyChoose(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Reason Card #{idx + 1}</h3>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Card Header Title</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={item.title}
                        onChange={(e) => updateWhyChooseField(idx, 'title', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Card Description</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[60px]"
                        value={item.description}
                        onChange={(e) => updateWhyChooseField(idx, 'description', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 8: Page FAQs */}
        {activeTab === 'faqs' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6 animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Page FAQ Content</h2>
              <button
                onClick={addFAQ}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add FAQ Item
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQ Grid Header Title</label>
                <input
                  className="input-modern"
                  value={form.faqsTitle}
                  onChange={(e) => updateField('faqsTitle', e.target.value)}
                  placeholder="Virtual CFO FAQs..."
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQ Grid Description</label>
                <input
                  className="input-modern"
                  value={form.faqsSubtitle}
                  onChange={(e) => updateField('faqsSubtitle', e.target.value)}
                  placeholder="Discover how our virtual CFO team guides..."
                />
              </div>
            </div>

            <div className="space-y-4 pt-4">
              {form.faqs.map((faq, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <button
                    onClick={() => removeFAQ(idx)}
                    className="absolute top-4 right-4 text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                  >
                    <FaTrash className="w-3 h-3" />
                  </button>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">FAQ Item #{idx + 1}</h3>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Question</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={faq.question}
                        onChange={(e) => updateFAQField(idx, 'question', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Answer</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[80px]"
                        value={faq.answer}
                        onChange={(e) => updateFAQField(idx, 'answer', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default CFOAdvisoryCMS;
